import { useState } from 'react';
import { X, Save, Loader2, Tag, AlertCircle } from 'lucide-react';
import { useTemplateStore } from '../../stores/templateStore';
import { useStudioStore } from '../../stores/studioStore';
import { TEMPLATE_CATEGORIES, type TemplateCategory } from '../../types';

interface SaveTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SaveTemplateModal({ isOpen, onClose }: SaveTemplateModalProps) {
  const { saveAsTemplate, isSaving } = useTemplateStore();
  const { canvasState, activeProject } = useStudioStore();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TemplateCategory>('custom');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isPublished, setIsPublished] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const dynamicFields = canvasState.elements
    .filter((el) => el.contentType === 'DYNAMIC' && 'dynamicField' in el && el.dynamicField)
    .map((el) => (el as any).dynamicField);

  const handleAddTag = () => {
    const trimmed = tagInput.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput('');
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a template name');
      return;
    }

    setError(null);
    const result = await saveAsTemplate({
      name: name.trim(),
      description: description.trim(),
      category,
      tags,
      isPublished,
      canvasState: canvasState as unknown as Record<string, unknown>,
      dynamicFields,
      projectId: activeProject?.id,
    });

    if (result) {
      setName('');
      setDescription('');
      setCategory('custom');
      setTags([]);
      setIsPublished(false);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[#111115] border border-white/[0.08] rounded-xl shadow-2xl w-[460px] max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Save size={18} className="text-cyan-400" />
            <h2 className="text-sm font-semibold text-white">Save as Template</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-white/40 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(85vh-130px)] p-5 space-y-4 sidebar-scroll">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/30 mb-1.5 font-semibold">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hero Banner - Fall Campaign"
              className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500/40 transition-colors"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/30 mb-1.5 font-semibold">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this template..."
              rows={3}
              className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500/40 transition-colors resize-none"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/30 mb-1.5 font-semibold">
              Category
            </label>
            <div className="grid grid-cols-3 gap-1.5">
              {TEMPLATE_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`text-xs py-1.5 rounded-lg transition-colors ${
                    category === cat.id
                      ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                      : 'bg-white/[0.03] text-white/40 border border-white/[0.06] hover:text-white/60 hover:border-white/[0.1]'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/30 mb-1.5 font-semibold">
              Tags
            </label>
            <div className="flex gap-1.5 mb-2 flex-wrap">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1 text-[11px] px-2 py-0.5 bg-white/[0.06] text-white/60 rounded-full"
                >
                  <Tag size={10} />
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-0.5 text-white/30 hover:text-white/60"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Add a tag..."
                className="flex-1 px-2.5 py-1.5 text-xs bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder:text-white/20 focus:outline-none focus:border-cyan-500/40 transition-colors"
              />
              <button
                onClick={handleAddTag}
                disabled={!tagInput.trim()}
                className="px-3 py-1.5 text-xs bg-white/[0.04] text-white/40 rounded-lg hover:bg-white/[0.08] hover:text-white/60 transition-colors disabled:opacity-30"
              >
                Add
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between py-2 px-3 bg-white/[0.02] rounded-lg border border-white/[0.04]">
            <div>
              <span className="text-xs text-white/60">Publish immediately</span>
              <p className="text-[10px] text-white/25 mt-0.5">Make available in template gallery</p>
            </div>
            <button
              onClick={() => setIsPublished(!isPublished)}
              className={`relative w-9 h-5 rounded-full transition-colors ${
                isPublished ? 'bg-cyan-500' : 'bg-white/[0.1]'
              }`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                  isPublished ? 'left-[18px]' : 'left-0.5'
                }`}
              />
            </button>
          </div>

          {dynamicFields.length > 0 && (
            <div className="py-2 px-3 bg-cyan-500/5 rounded-lg border border-cyan-500/10">
              <span className="text-[11px] text-cyan-400">
                {dynamicFields.length} dynamic field{dynamicFields.length !== 1 ? 's' : ''} will be included
              </span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 py-2 px-3 bg-red-500/10 rounded-lg border border-red-500/20">
              <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
              <span className="text-[11px] text-red-400">{error}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs text-white/40 hover:text-white/60 rounded-lg hover:bg-white/[0.04] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-cyan-500/15 text-cyan-400 rounded-lg hover:bg-cyan-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            {isSaving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  );
}
